import React from "react"
import { DetailsPopup } from "modules/common/components/form/DetailsPopup"
import { Block } from "modules/common/components/page/Block"
import { matchRoute } from "modules/common/services/routing";
import { WhenPath } from "modules/common/components/routing/WhenPath";
import { routes } from "modules/common/routes"
import { UnregisterCallback, History, Location } from "modules/common/components/routing/withRouter";
import { observer } from "mobx-react"
import { texts } from "modules/common/texts"
import { EMPTY_OBJECT_ID } from "modules/common/constants"
import { EmployeeStoreType } from "../models/employee-store"
import { EmployerDetails } from "./details/EmployerDetails"
import { EmployerList } from "./list/EmployerList"
import { menuItems } from "modules/root/components/menu/MainMenu"
import func from "../../functionalities"
import sessionFunc from "modules/session/functionalities"
import { can } from "modules/session/auth/access"
import { Search } from "modules/common/components/form/Search"
import { RemovedList } from "./list/RemovedList"
import { Dialog, Classes, Spinner } from "@blueprintjs/core"
import moment from "moment"
import styles from "./EmployeePage.module.scss"
import { PlanrButton } from "modules/common/components/planr/button/Button"
import { OwnPremiumsWidget } from "modules/main/components/own-premiums/OwnPremiumsWidget"
import { OwnMaterialsWidget } from "modules/main/components/own-materials/OwnMaterialsWidget"
import { canReadOrder } from "modules/orders-manage/functionalities"
import { MaterialValuesStoreType } from "modules/dictionaries/inventory/models/material-value-store"
import { isNewlyCreated } from "modules/common/models/entity"
import { MenuItem } from "@blueprintjs/core"
import { ItemRenderer, MultiSelect } from "@blueprintjs/select"
import { filterItemPredicate, matchTargetWidthPopover } from "modules/common/services/form/select"
import { RoleDetails } from "modules/session/access/components/roles/RoleDetails"
import { SessionType } from "modules/session/auth/models/session"
import { formatMoney } from "modules/common/components/money/Money"
import { OneCImportDialog, EMPLOYEE_ROLES } from "modules/spending/onec/components/OneCImportDialog"
import { OneCStoreType } from "modules/spending/onec/models/onec-store"
import { withRouter } from "modules/common/components/routing/withRouter";
import { LocalLoader } from "modules/common/components/loader/Loader"

const menu = menuItems()

const NotesMultiSelect = MultiSelect.ofType<SelectItem>()

/** Выпадающий список ровно по ширине поля */
const MATCH_TARGET_WIDTH = matchTargetWidthPopover()

// Объединённая хронология «должность + ранг»: одна строка на каждую смену должности
// или ранга. Для строки-ранга подставляется должность, действовавшая на ту дату, и наоборот.
const parseSince = (s: string) =>
  moment(s, ["DD.MM.YYYY", "DD.MM.YYYY HH:mm:ss", moment.ISO_8601], true)

function buildPositionRankHistory(positions: any, grades: any) {
  const posList: any[] = (positions ? Array.from(positions) : []).filter((p: any) => p && p.since)
  const gradeList: any[] = (grades ? Array.from(grades) : []).filter((g: any) => g && g.since)

  const resolveLatest = (list: any[], d: number) => {
    let best: any = null
    let bestD = -Infinity
    for (const item of list) {
      const id = parseSince(item.since).valueOf()
      if (id <= d && id >= bestD) {
        best = item
        bestD = id
      }
    }
    return best
  }

  const rankLabel = (g: any) =>
    g && g.gradeGuid && g.gradeGuid !== "null" && g.percent > 0
      ? `${(g.gradeName || "").trim()} — ${g.percent}%`
      : "-"

  const events = [
    ...posList.map((p) => ({ d: parseSince(p.since).valueOf(), since: p.since, kind: "position", ref: p })),
    ...gradeList.map((g) => ({ d: parseSince(g.since).valueOf(), since: g.since, kind: "grade", ref: g })),
  ].sort((a, b) => b.d - a.d)

  return events.map((e) => {
    const pos = e.kind === "position" ? e.ref : resolveLatest(posList, e.d)
    const grade = e.kind === "grade" ? e.ref : resolveLatest(gradeList, e.d)
    return {
      name: pos ? pos.name : "",
      department: pos && pos.department ? pos.department.name : "",
      rank: rankLabel(grade),
      since: e.since,
      salary: pos ? pos.salary : 0,
      bonus: pos ? pos.bonus : 0,
    }
  })
}

class EmployeePage extends React.Component<EmployeePageProps, EmployeePageState> {
  private unregister: UnregisterCallback | null = null;
  constructor(props: EmployeePageProps) {
    super(props)

    this.state = {
      employerId: getSelectedEmployerId(props.location.pathname),
      showRemoved: false,
      showPremiums: false,
      showPosition: false,
      showMaterials: false,
      printNotes: [],
      showPrint: false,
      loadingPrint: false,
      showAccess: false,
      loading: false,
      showImport: false,
    }
  }

  async componentDidMount() {
    document.title = menu.spending.nested.employee.label

    const { history, store } = this.props
    const { employerId } = this.state
    store.init(employerId)

    this.unregister = history.listen(async (location) => {
      const userId = getSelectedEmployerId(location.pathname)

      if (this.state.employerId !== userId) {
        this.setState({ employerId: userId, loading: true })

        await this.loadDetails(userId)
        this.setState({ loading: false })
      }
    })
  }

  componentWillUnmount() {
    this.unregister && this.unregister()
  }
  // openFolder = () => {
  //   const path = "\\\\10.136.10.10\\Obmen"

  //   // Пробуем открыть через file://
  //   const url = `file:\\\\${path}`

  //   // Способ 1: через window.open
  //   const newWindow = window.open(url)

  //   // Способ 2: через создание ссылки
  //   const link = document.createElement('a')
  //   link.href = url
  //   link.click()

  //   // Если не работает — выводим инструкцию
  //   setTimeout(() => {
  //     alert("Если папка не открылась:\n1. Скопируйте путь:\n" + path + "\n2. Нажмите Win+R и вставьте его")
  //   }, 1000)
  // };
  render() {
    const { store, access, baseUrl, materials } = this.props
    const { showRemoved, showPremiums, showMaterials, showAccess, loading, showPosition } = this.state
    const canChange = can(func.EMPLOYER_EDIT, access)
    const canImport = can(func.ONEC_IMPORT, access)
    const orderAccess = canReadOrder(access)

    return (
      <Block columned={true} className={styles.page}>
        {/* список сотрудников приходит долго: без индикатора страница выглядит пустой */}
        <LocalLoader active={store.isLoading} text="Загрузка сотрудников..." />

        <WhenPath path={routes.spending.employee.details()}>
          {this.state.employerId && !loading && (
            <DetailsPopup title={this.title(store.details.name)} onCancel={this.onEmployerSelected} width={1310}>
              <EmployerDetails
                baseUrl={baseUrl}
                positions={store.positions}
                employer={store.details}
                onSaved={this.onEmployerSaved}
                onRemoved={this.onEmployerRemoved}
                canChange={canChange}
                onPrintCredentials={store.printCredentials}
                spendings={store.projects}
                orderAccess={orderAccess}
                employerList={store.list}
                employerId={this.state.employerId}
                portfolios={store.portfolios}
                documentNotes={store.sortedDocumentNotes}
              />
            </DetailsPopup>
          )}

          <Dialog
            icon="info-sign"
            title="Должности"
            canEscapeKeyClose={true}
            canOutsideClickClose={false}
            isOpen={showPosition}
            onClose={this.hidePosition}
            style={{ width: `1180px` }}
            backdropClassName="standard"
          >
            <div className={`${Classes.DIALOG_BODY}`}>
              <div className={styles.positions}>
                <div className={styles.rowPosition} style={{ display: "flex", fontWeight: "bold" }}>
                  <div className={styles.positionName}>Должность</div>
                  <div className={styles.positionName}>Отдел</div>
                  <div className={styles.positionName}>Ранг</div>
                  <div className={styles.positionMoney}>Дата с</div>
                  <div className={styles.positionMoney}>Оклад</div>
                  <div className={styles.positionMoney}>Премия</div>
                </div>
                {buildPositionRankHistory(store.details.employerPositions, store.details.gradeHistory).map(
                  (row, index) => (
                    <div key={index} className={styles.rowPosition} style={{ display: "flex" }}>
                      <div className={styles.positionName}>{row.name}</div>
                      <div className={styles.positionName}>{row.department}</div>
                      <div className={styles.positionName}>{row.rank}</div>
                      <div className={styles.positionMoney}>{row.since}</div>
                      <div className={styles.positionMoney}>{row.salary !== 0 ? formatMoney(row.salary) : "-"}</div>
                      <div className={styles.positionMoney}>{row.bonus !== 0 ? formatMoney(row.bonus) : "-"}</div>
                    </div>
                  )
                )}
              </div>
            </div>
          </Dialog>
          <Dialog
            icon="info-sign"
            title="Назначенные премии"
            canEscapeKeyClose={true}
            canOutsideClickClose={false}
            isOpen={showPremiums}
            onClose={this.hidePremiums}
            style={{ width: `600px` }}
            backdropClassName="standard"
          >
            <div className={Classes.DIALOG_BODY}>
              <OwnPremiumsWidget store={store.premiums} orderAccess={orderAccess} />
            </div>
          </Dialog>

          <Dialog
            icon="info-sign"
            title="Закрепленный инвентарь"
            canEscapeKeyClose={true}
            canOutsideClickClose={false}
            isOpen={showMaterials}
            onClose={this.hideMaterials}
            style={{ width: "600px" }}
            backdropClassName="standard"
          >
            <div className={Classes.DIALOG_BODY}>
              <OwnMaterialsWidget store={materials.list} orderAccess={orderAccess} employerId={this.state.employerId} />
            </div>
          </Dialog>

          <Dialog
            icon="info-sign"
            title="Права доступа"
            canEscapeKeyClose={true}
            canOutsideClickClose={false}
            isOpen={showAccess}
            onClose={this.hideAccess}
            style={{ width: `1180px` }}
            backdropClassName="standard"
          >
            <div className={`${Classes.DIALOG_BODY} ${styles.userAccess}`}>
              <RoleDetails role={store.details.role} columns={store.accessColumns} onToggle={this.setFunctionality} />
            </div>
          </Dialog>
        </WhenPath>
        <Dialog
          icon="info-sign"
          title="Печать документов"
          canEscapeKeyClose={true}
          canOutsideClickClose={false}
          isOpen={this.state.showPrint}
          onClose={this.printHide}
          style={{ width: `600px` }}
          backdropClassName="standard"
        >
          <div className={Classes.DIALOG_BODY} style={{ margin: "auto", width: "100%" }}>
            <div className={styles.documentSelector}>
              <NotesSelect
                notes={store.sortedDocumentNotes}
                selected={this.state.printNotes}
                onChange={(printNotes) => this.setState({ printNotes })}
              />
            </div>
            {this.state.loadingPrint && (
              <div style={{ margin: "0 auto", display: "flex" }}>
                <span className={styles.spanDocumentSelector} style={{ textAlign: "right", marginLeft: "65px" }}>
                  Загрузка документов...
                </span>
                {<Spinner intent="primary" size={25} />}
              </div>
            )}
            {!this.state.loadingPrint && !!store.sortedDocumentNotes.length && (
              <PlanrButton
                type="blueish"
                style={{ margin: "0 auto" }}
                disabled={!this.state.printNotes.length}
                onClick={async () => {
                  this.setState({ loadingPrint: true })
                  await store.printDocuments(this.state.printNotes)

                  this.setState({ loadingPrint: false })
                }}
              >
                Распечатать
              </PlanrButton>
            )}

            {!store.sortedDocumentNotes.length && (
              <span className={styles.spanDocumentSelector}>Документы сотрудников пока не загружены</span>
            )}
          </div>
        </Dialog>

        <div className={styles.tools}>
          {canChange && !showRemoved && (
            <PlanrButton
              type="secondary"
              size="small"
              icon="general-plus-big"
              onClick={this.onEmployerAdd}
              style={{
                width: "230px",
                marginBottom: "32px",
                borderRadius: "120px",
              }}
            >{`${texts.add} сотрудника`}</PlanrButton>
          )}

          {canImport && !showRemoved && (
            <PlanrButton
              type="graybtn"
              size="small"
              icon="general-import"
              onClick={() => this.setState({ showImport: true })}
              style={{
                width: "200px",
                marginBottom: "32px",
                borderRadius: "120px",
              }}
            >
              Импорт из 1С
            </PlanrButton>
          )}

          <OneCImportDialog
            isOpen={this.state.showImport}
            onClose={() => this.setState({ showImport: false })}
            store={this.props.onec}
            entityKey="employees"
            title="Импорт сотрудников из 1С"
            roles={EMPLOYEE_ROLES}
            onImported={() => store.list.load()}
          />

          <PlanrButton
            type="graybtn"
            size="small"
            icon="general-ppl"
            onClick={this.toggleVisibility}
            className={styles.toggler}
            style={{
              width: "260px",
              marginBottom: "32px",
              borderRadius: "120px",
            }}
          >
            {showRemoved ? "Показать активных" : "Показать удаленных"}
          </PlanrButton>

          <div className={styles.right}>
            <PlanrButton
              type="graybtn"
              icon="general-export"
              onClick={store.export}
              title={texts.export}
              round={true}
            />
            <PlanrButton type="graybtn" icon="general-print" round={true} onClick={this.printShow} title="Печать" />
            <Search query={store.list.query} onSearch={store.list.setQuery} />
          </div>
        </div>
        <EmployerList
          store={store.list}
          onEmployerSelected={this.onEmployerSelected}
          hidden={showRemoved}
          onEmployerRemove={this.onEmployerRemove}
          canChange={canChange}
          portfolios={store.portfolios}
        />

        <RemovedList
          store={store.removed}
          onEmployerSelected={this.onEmployerSelected}
          hidden={!showRemoved}
          onEmployerRestore={this.onEmployerRestore}
          canChange={canChange}
        />
      </Block>
    )
  }

  title = (header: string) => {
    const showButtons = !isNewlyCreated(this.state.employerId)
    const canAccess = showButtons && can(sessionFunc.ACCESS_EDIT, this.props.access)

    return (
      <div className={styles.title}>
        {header}
        {showButtons && this.props.store.details.employerPositions.length > 1 && (
          <PlanrButton type="secondary" size="small" onClick={this.showPosition}>
            Должности
          </PlanrButton>
        )}
        {showButtons && (
          <PlanrButton type="secondary" size="small" onClick={this.showPremiums}>
            Назначенные премии
          </PlanrButton>
        )}
        {showButtons && (
          <PlanrButton type="secondary" size="small" onClick={this.showMaterials}>
            Закрепленный инвентарь
          </PlanrButton>
        )}
        {canAccess && (
          <PlanrButton type="secondary" size="small" onClick={this.showAccess}>
            Права доступа
          </PlanrButton>
        )}
      </div>
    )
  };

  printHide = () => this.setState({ showPrint: false });

  printShow = () => {
    // примечания завели минуту назад в карточке — список должен быть свежим
    this.props.store.loadDocumentNotes()
    this.setState({ showPrint: true })
  };

  showPremiums = () => this.setState({ showPremiums: true });
  hidePremiums = () => this.setState({ showPremiums: false });

  showMaterials = () => this.setState({ showMaterials: true });
  hideMaterials = () => this.setState({ showMaterials: false });

  showAccess = () => this.setState({ showAccess: true });
  hideAccess = () => this.setState({ showAccess: false });

  showPosition = () => this.setState({ showPosition: true });
  hidePosition = () => this.setState({ showPosition: false });

  toggleVisibility = () => this.setState({ showRemoved: !this.state.showRemoved });

  onEmployerAdd = () => {
    this.onEmployerSelected(EMPTY_OBJECT_ID)
  };

  onEmployerSelected = (id: string = "") => {
    const { history } = this.props

    const url = id ? routes.spending.employee.details(id) : routes.spending.employee.path

    history.push(url)
  };

  onEmployerRestore = async (id: string = "") => {
    const user = this.props.store.removed.asMap[id]
    if (user) {
      const ok = await user.restore()
      if (ok) {
        this.loadList()
        this.loadRemovedList()
      }
    }
  };

  onEmployerRemove = async (id: string = "") => {
    const user = this.props.store.list.asMap[id]
    if (user) {
      const ok = await user.delete()
      if (ok) {
        this.onEmployerRemoved()
      }
    }
  };

  onEmployerSaved = async (id: string) => {
    this.onEmployerSelected(id)
    await this.loadList()
  };

  onEmployerRemoved = () => {
    const { history } = this.props

    this.loadList()
    this.loadRemovedList()

    const url = routes.spending.employee.path
    history.push(url)
  };

  loadList = () => this.props.store.list.load();

  loadRemovedList = () => this.props.store.removed.load();

  loadDetails = async (id: string) => {
    await this.props.store.details.load(id)
    await this.props.store.premiums.load(id)
    this.props.store.loadSpendings(id)
  };

  setFunctionality = (roleId: string, functionality: string, state: boolean) =>
    this.props.store.details.setFunctionality(functionality, state);
}

export default withRouter(observer(EmployeePage))

function getSelectedEmployerId(pathname: string) {
  const match = matchRoute<EmployeeParams>(pathname, routes.spending.employee.details())

  return match && match.params ? match.params.employerId : ""
}

interface EmployeePageProps {
  baseUrl: string
  store: EmployeeStoreType
  access: string[]
  materials: MaterialValuesStoreType
  history: History
  session: SessionType
  location: Location
  onec: OneCStoreType
}

interface EmployeeParams {
  employerId: string
}

interface EmployeePageState extends EmployeeParams {
  showRemoved: boolean
  showPremiums: boolean
  showMaterials: boolean
  showAccess: boolean
  showPosition: boolean
  printNotes: string[]
  showPrint: boolean
  loading: boolean
  loadingPrint: boolean
  showImport: boolean
}

/**
 * Документы для массовой печати: выбирается сразу несколько примечаний — одни и те же
 * документы у разных сотрудников подписаны по-разному (опечатки, разные формулировки)
 */
const NotesSelect = ({ notes, selected, onChange }: NotesSelectProps) => {
  const items = notes.map((note) => ({ id: note, label: note }))

  const toggle = (note: string) =>
    onChange(selected.includes(note) ? selected.filter((item) => item !== note) : [...selected, note])

  return (
    <NotesMultiSelect
      className="full-width-select"
      fill={true}
      items={items}
      itemPredicate={filterItemPredicate}
      itemRenderer={renderCheckedNote(selected)}
      itemsEqual={(a: SelectItem, b: SelectItem) => a.id === b.id}
      noResults={<MenuItem disabled={true} text="Ничего не найдено" />}
      onItemSelect={(item: SelectItem) => toggle(item.id)}
      placeholder="Выберите документы"
      popoverProps={MATCH_TARGET_WIDTH}
      // список остаётся открытым, поэтому активный пункт не сбрасываем
      resetOnSelect={false}
      selectedItems={selected.map((note) => ({ id: note, label: note }))}
      tagRenderer={(item: SelectItem) => item.label}
      tagInputProps={{
        onRemove: (_value: any, index: number) => onChange(selected.filter((_, i) => i !== index)),
      }}
    />
  )
}

/** Пункт списка с отметкой выбранного */
const renderCheckedNote = (selected: string[]): ItemRenderer<SelectItem> => (item, { handleClick, modifiers }) => {
  if (!modifiers.matchesPredicate) {
    return null
  }

  return (
    <MenuItem
      key={item.id}
      disabled={modifiers.disabled}
      icon={selected.includes(item.id) ? "tick" : "blank"}
      onClick={handleClick}
      shouldDismissPopover={false}
      text={item.label}
    />
  )
}

interface NotesSelectProps {
  notes: string[]
  selected: string[]
  onChange: (notes: string[]) => void
}
